import { Link } from '@tanstack/react-router'
import { ChevronRight, ExternalLink } from 'lucide-react'
import type { ReactNode } from 'react'

import { cn } from '@/lib/utils'
import type { SettingsTab } from '@/screens/account/components/account-tabs'

/**
 * Where a row goes when it goes somewhere.
 *
 * A closed union rather than a bare `to`/`href` pair: TanStack's `Link` types
 * `search` against the route named by `to`, so a component that took an
 * arbitrary `to` would have to widen `search` to `any` and lose the one check
 * that stops `?tab=securtiy` shipping.
 */
export type RowLink =
  | { kind: 'settings'; tab: SettingsTab }
  | { kind: 'route'; to: '/profile' | '/subscription' }
  | { kind: 'external'; href: string }

interface SettingsRowProps {
  /** A 20px glyph. The hub draws one per row; the sub-pages draw none. */
  icon?: ReactNode
  title: string
  description?: string
  /** The muted right-hand summary: "On, 6 topics", "System, English", "1.0.0". */
  value?: ReactNode
  /** Replaces `value`: a Toggle, a pair of channel chips, a small button. */
  action?: ReactNode
  link?: RowLink
  onClick?: () => void
  /** Delete account and the like: the title goes destructive, nothing else. */
  danger?: boolean
  className?: string
}

/**
 * One row inside a `SettingsGroup`.
 *
 * ## The rule this component encodes
 *
 * A chevron is drawn if and only if the row actually goes somewhere. V2 draws
 * one on rows whose destination this app has not built — Quiet hours, Number
 * format, App PIN — and following those pixels would put a `›` on a dozen rows
 * that do nothing when clicked. A chevron is a promise; an unfulfilled one is
 * worse than a plain row. So an inert row renders as a `div` with its honest
 * value and no affordance, and the report lists every one of them.
 *
 * The three shapes are a Link, a button or a div depending on what the caller
 * gives it, for the reason `ListRow` gives: a div with an onClick is unreachable
 * by keyboard, and a link that is really a button breaks middle-click.
 */
export default function SettingsRow({
  icon,
  title,
  description,
  value,
  action,
  link,
  onClick,
  danger = false,
  className,
}: SettingsRowProps) {
  const interactive = link !== undefined || onClick !== undefined

  const layout = cn(
    'flex w-full items-center gap-3 border-b border-border-subtle py-3 text-left last:border-b-0',
    description === undefined ? 'min-h-11' : 'min-h-16',
    interactive && [
      // Mock `ac-row::before` (COMPARE-ac §1 Hover states): the raised #1a1a1a fading in under the
      // row and bleeding to the card's edge (`--row-bleed`, set by SettingsGroup), square. A layer
      // rather than the row's own fill, so the wash can pass the row's box without moving it;
      // `isolate` keeps its `-z-10` behind this row's text and above the card. The chevron
      // turns white and slides 2px (kit `chevron-nudge`). Keyboard focus washes too; the ring
      // stays the kit's (A2).
      'chevron-nudge group/row relative isolate cursor-pointer',
      "before:absolute before:-inset-x-(--row-bleed,--spacing(5)) before:inset-y-0 before:-z-10 before:bg-popover before:opacity-0 before:transition-opacity before:duration-fast before:content-['']",
      'hover:before:opacity-100 focus-visible:before:opacity-100',
    ],
    className,
  )

  const trail = cn(
    'size-4 shrink-0 text-muted-foreground-subtle transition-[color,translate] duration-med ease-out group-hover/row:text-foreground group-focus-visible/row:text-foreground',
  )

  const body = (
    <>
      {icon === undefined ? null : (
        <span className="flex size-5 shrink-0 items-center justify-center text-muted-foreground">
          {icon}
        </span>
      )}
      <span className="min-w-0 flex-1">
        <span className={cn('block text-body-m', danger && 'text-destructive')}>{title}</span>
        {description === undefined ? null : (
          <span className="block text-body-xs text-muted-foreground-subtle">{description}</span>
        )}
      </span>
      {action ??
        (value === undefined ? null : (
          <span className="shrink-0 text-body-m text-muted-foreground-subtle">{value}</span>
        ))}
      {link?.kind === 'external' ? (
        <ExternalLink data-nudge="" className={trail} aria-hidden="true" />
      ) : interactive ? (
        <ChevronRight data-nudge="" className={trail} aria-hidden="true" />
      ) : null}
    </>
  )

  if (link?.kind === 'settings') {
    return (
      <Link to="/settings" search={{ tab: link.tab }} className={layout}>
        {body}
      </Link>
    )
  }
  if (link?.kind === 'route') {
    return (
      <Link to={link.to} className={layout}>
        {body}
      </Link>
    )
  }
  if (link?.kind === 'external') {
    return (
      // Opened in a new tab because these leave the app for a policy page, and
      // `noreferrer` with it: `target="_blank"` alone hands the opened document
      // a live `window.opener` back into the signed-in app.
      <a href={link.href} target="_blank" rel="noreferrer" className={layout}>
        {body}
      </a>
    )
  }
  if (onClick !== undefined) {
    return (
      <button type="button" onClick={onClick} className={cn(layout, 'cursor-pointer')}>
        {body}
      </button>
    )
  }
  return <div className={layout}>{body}</div>
}
